import { useCallback, useMemo, useState } from 'react';
import { FormControl, Grid2 as Grid } from '@mui/material';

import AccessGuard from '../../../../components/AccessGuard';

import { AGENCY_OEM, USER_GROUP_ADMIN, USER_GROUP_ADVERTISER, USER_GROUP_AGENCY } from '../../../../types/User';
import { Agency } from '../../../../types/Agency';
import { Dealer as Advertiser } from '../../../../types/Dealer';
import { Oem } from '../../../../types/Oem';
import { OemProgram } from '../../../../types/DspEntity';
import { ReportingCriteria } from '../../../../types/Criteria/ReportingCriteria';
import { useReportingDashboardPageContext } from '../../../../hooks/useReportingDashboardPage';
import AdvertiserAutocomplete from '../../../../components/AdvertiserAutocomplete';
import AgencyAutocomplete from '../../../../components/AgencyAutocomplete';
import OemCheckboxAutoComplete from '../../../../components/OemCheckboxAutoComplete';
import OemProgramCheckboxAutoComplete from '../../../../components/OemProgramCheckboxAutoComplete';
import ReportingCampaignStrategyTypeSelect from './ReportingCampaignStrategyTypeSelect';
import ReportingCriteriaDataRangePicker from './ReportingCriteriaDataRangePicker';
import { AgencyCriteria } from '../../../../types/Criteria/AgencyCriteria';
import Column from '../../../../components/Column';
import AgencyMarketRegionCheckboxAutoComplete from '../../../../components/AgencyMarketRegionCheckboxAutocomplete';
import { OemCriteria } from '../../../../types/Criteria/OemCriteria';
import { OemProgramCriteria } from '../../../../types/Criteria/OemProgramCriteria';
import { AgencyMarketRegionCriteria } from '../../../../types/Criteria/AgencyMarketRegionCriteria';
import { useUserContext } from '../../../../hooks/Contexts/UserContext';
import { STRATEGY_TYPES } from '../../../../types/StrategyType';

type ReportingCriteriaFormProps = {
    reportingCriteria: ReportingCriteria;
    onChangeReportingCriteria: (reportingCriteria: ReportingCriteria) => void;
    onFetchAdvertisers?: (advertisers: Advertiser[]) => void;
    onFetchingAdvertisers?: () => void;
};

export default function ReportingCriteriaForm(props: ReportingCriteriaFormProps) {
    const { reportingCriteria, onChangeReportingCriteria, onFetchAdvertisers, onFetchingAdvertisers } = props;

    const { userContext } = useUserContext();

    const { defaultMinDate, defaultMaxDate, isReportingCriteriaSetup, setIsReportingCriteriaSetup } =
        useReportingDashboardPageContext();

    const [isOemVisible, setIsOemVisible] = useState<boolean>(true);
    const [isDmaVisible, setIsDmaVisible] = useState<boolean>(true);
    const [isOemVisibilityInitialized, setIsOemVisibilityInitialized] = useState<boolean>(false);
    const [isDmaVisibilityInitialized, setIsDmaVisibilityInitialized] = useState<boolean>(false);

    const handleAgencyOnChange = useCallback(
        (agencies: Agency[]): void => {
            onChangeReportingCriteria({
                ...reportingCriteria,
                agencyIds: agencies.map((agency: Agency) => agency.id as number),
                agencyNames: agencies.map((agency: Agency) => agency.name),
            });
        },
        [reportingCriteria, onChangeReportingCriteria]
    );

    const handleOemOnChange = useCallback(
        (oems: Oem[]): void => {
            onChangeReportingCriteria({
                ...reportingCriteria,
                oemIds: oems.map((oem: Oem) => oem.id as number),
                oemNames: oems.map((oem: Oem) => oem.name),
            });
        },
        [reportingCriteria, onChangeReportingCriteria]
    );

    const handleOemProgramOnChange = useCallback(
        (oemPrograms: OemProgram[]): void => {
            if (!isReportingCriteriaSetup) {
                setIsReportingCriteriaSetup(true);
            }
            onChangeReportingCriteria({
                ...reportingCriteria,
                oemProgramIds: oemPrograms.map((oemProgram: OemProgram) => oemProgram.id as number),
                oemProgramNames: oemPrograms.map((oemProgram: OemProgram) => oemProgram.oemProgram),
            });
        },
        [reportingCriteria, onChangeReportingCriteria]
    );

    const handleDmaOnChange = useCallback(
        (value: string[] | string | null): void => {
            if (!isReportingCriteriaSetup) {
                setIsReportingCriteriaSetup(true);
            }
            onChangeReportingCriteria({
                ...reportingCriteria,
                agencyMarketRegions: Array.isArray(value) ? value : value ? [value] : [],
            });
        },
        [reportingCriteria, onChangeReportingCriteria]
    );

    const handleAdvertiserOnChange = useCallback(
        (advertisers: Advertiser[]) => {
            if (onFetchAdvertisers) {
                onFetchAdvertisers(advertisers);
            }

            if (!isReportingCriteriaSetup) {
                setIsReportingCriteriaSetup(true);
            }

            onChangeReportingCriteria({
                ...reportingCriteria,
                dealerIds: advertisers.map((advertiser: Advertiser) => advertiser.id as number),
                dealerNames: advertisers.map((advertiser: Advertiser) => advertiser.dealerName),
            });
        },
        [
            reportingCriteria,
            onChangeReportingCriteria,
            onFetchAdvertisers,
            setIsReportingCriteriaSetup,
            isReportingCriteriaSetup,
        ]
    );

    const oemCriteria = useMemo((): OemCriteria => {
        return {
            agencyIds: reportingCriteria.agencyIds,
            advertiserIds: reportingCriteria.dealerIds,
            oemProgramIds: reportingCriteria.oemProgramIds,
            agencyMarketRegions: reportingCriteria.agencyMarketRegions,
        };
    }, [reportingCriteria]);

    const oemProgramCriteria = useMemo((): OemProgramCriteria => {
        return {
            agencyIds: reportingCriteria.agencyIds,
            advertiserIds: reportingCriteria.dealerIds,
            oemIds: reportingCriteria.oemIds,
            agencyMarketRegions: reportingCriteria.agencyMarketRegions,
        };
    }, [reportingCriteria]);

    const agencyMarketRegionCriteria = useMemo((): AgencyMarketRegionCriteria => {
        return {
            agencyIds: reportingCriteria.agencyIds,
            advertiserIds: reportingCriteria.dealerIds,
            oemIds: reportingCriteria.oemIds,
            oemProgramIds: reportingCriteria.oemProgramIds,
        };
    }, [reportingCriteria]);

    const agencyCriteria = useMemo((): AgencyCriteria => {
        return {
            advertiserIds: reportingCriteria.dealerIds,
            oemIds: reportingCriteria.oemIds,
            oemProgramIds: reportingCriteria.oemProgramIds,
            agencyMarketRegions: reportingCriteria.agencyMarketRegions,
        };
    }, [reportingCriteria]);

    const advertiserCriteria = useMemo(() => {
        return {
            agencyIds: reportingCriteria.agencyIds,
            oemIds: reportingCriteria.oemIds,
            oemProgramIds: reportingCriteria.oemProgramIds,
            agencyMarketRegions: reportingCriteria.agencyMarketRegions,
        };
    }, [reportingCriteria]);

    return (
        <Column gap={2}>
            <Grid container spacing={2}>
                <AccessGuard
                    accessGroup={USER_GROUP_ADMIN + ',' + USER_GROUP_AGENCY + ',' + USER_GROUP_ADVERTISER}
                    accessAgency={AGENCY_OEM}
                >
                    <Grid size={userContext.isAdmin() ? 3 : 4} sx={{ display: isOemVisible ? 'inline-flex' : 'none' }}>
                        <FormControl fullWidth>
                            <OemCheckboxAutoComplete
                                // autoSelectSingleOption
                                multiple
                                mass
                                deferChangeUntilClose
                                size="small"
                                value={reportingCriteria.oemIds}
                                criteria={oemCriteria}
                                onChange={handleOemOnChange}
                                onEmpty={() => {
                                    if (!userContext.isAdmin() && !isOemVisibilityInitialized) {
                                        setIsOemVisible(false);
                                        setIsOemVisibilityInitialized(true);
                                    }
                                }}
                                onNotEmpty={() => {
                                    if (!userContext.isAdmin() && !isOemVisibilityInitialized) {
                                        setIsOemVisible(true);
                                        setIsOemVisibilityInitialized(true);
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>
                </AccessGuard>

                <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                    <Grid size={userContext.isAdmin() ? 3 : 4}>
                        <FormControl fullWidth sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
                            <OemProgramCheckboxAutoComplete
                                multiple
                                mass
                                deferChangeUntilClose
                                size="small"
                                value={reportingCriteria.oemProgramIds}
                                criteria={oemProgramCriteria}
                                onChange={handleOemProgramOnChange}
                            />
                        </FormControl>
                    </Grid>
                </AccessGuard>

                <AccessGuard accessGroup={USER_GROUP_ADMIN}>
                    <Grid size={userContext.isAdmin() ? 3 : 4}>
                        <FormControl fullWidth sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
                            <AgencyAutocomplete
                                multiple
                                mass
                                deferChangeUntilClose
                                size="small"
                                value={reportingCriteria.agencyIds}
                                criteria={agencyCriteria}
                                onChange={handleAgencyOnChange}
                            />
                        </FormControl>
                    </Grid>
                </AccessGuard>

                {!userContext.isAdvertiser() && (
                    <AccessGuard accessGroup={USER_GROUP_ADMIN + ',' + USER_GROUP_AGENCY}>
                        <Grid
                            size={userContext.isAdmin() ? 3 : 4}
                            sx={{ display: isDmaVisible ? 'inline-flex' : 'none' }}
                        >
                            <FormControl fullWidth>
                                <AgencyMarketRegionCheckboxAutoComplete
                                    multiple
                                    mass
                                    deferChangeUntilClose
                                    size="small"
                                    value={reportingCriteria.agencyMarketRegions}
                                    criteria={agencyMarketRegionCriteria}
                                    onChange={handleDmaOnChange}
                                    onEmpty={() => {
                                        if (!userContext.isAdmin() && !isDmaVisibilityInitialized) {
                                            setIsDmaVisible(false);
                                            setIsDmaVisibilityInitialized(true);
                                        }
                                    }}
                                    onNotEmpty={() => {
                                        if (!userContext.isAdmin() && !isDmaVisibilityInitialized) {
                                            setIsDmaVisible(true);
                                            setIsDmaVisibilityInitialized(true);
                                        }
                                    }}
                                />
                            </FormControl>
                        </Grid>
                    </AccessGuard>
                )}
            </Grid>

            <Grid container spacing={2}>
                <Grid size={4}>
                    <FormControl fullWidth sx={{ '& .MuiAutocomplete-root': { width: '100%' } }}>
                        <AdvertiserAutocomplete
                            // autoSelectSingleOption
                            multiple
                            mass
                            deferChangeUntilClose
                            size="small"
                            value={reportingCriteria.dealerIds}
                            criteria={advertiserCriteria}
                            onChange={handleAdvertiserOnChange}
                            onLoading={onFetchingAdvertisers}
                            showInactiveAdvertisersToggle={true}
                        />
                    </FormControl>
                </Grid>
                <Grid size={4}>
                    <FormControl fullWidth>
                        <ReportingCampaignStrategyTypeSelect
                            strategyTypes={STRATEGY_TYPES}
                            reportingCriteria={reportingCriteria}
                            onChangeReportingCriteria={onChangeReportingCriteria}
                        />
                    </FormControl>
                </Grid>

                <Grid size={4}>
                    <FormControl fullWidth>
                        <ReportingCriteriaDataRangePicker
                            minDate={defaultMinDate}
                            maxDate={defaultMaxDate}
                            startDate={reportingCriteria.startDate}
                            endDate={reportingCriteria.endDate}
                            onChange={(startDate?: Date, endDate?: Date): void => {
                                const isFilled = startDate !== undefined && endDate !== undefined;
                                if (isFilled) {
                                    onChangeReportingCriteria({
                                        ...reportingCriteria,
                                        startDate: startDate ?? reportingCriteria.startDate,
                                        endDate: endDate ?? reportingCriteria.endDate,
                                    });
                                }
                            }}
                        />
                    </FormControl>
                </Grid>
            </Grid>
        </Column>
    );
}
